import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Calendar,
  DollarSign,
  Building2,
  User,
  Mail,
  Phone,
  FileText,
  Activity as ActivityIcon,
  Clock,
  TrendingUp,
  Target,
  Edit,
  Users,
  MapPin,
  Globe,
  Trash2,
  CheckCircle,
  XCircle,
  Percent,
  Tag,
  ArrowRightLeft,
  ShieldCheck,
  Send,
  Copy,
  FileCheck,
  History,
  AlertCircle,
  FileSignature,
  Loader2,
  X,
  Plus,
  Wrench,
  Boxes,
  Eye as EyeIcon,
  Download
} from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import { es } from "date-fns/locale";
import { useEffect, useState, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  opportunityAPI,
  Opportunity,
  OpportunityStage,
} from "@/services/opportunityService";
import { usersService } from "@/services/usersService";
import { inventoryService } from "@/services/inventoryService";
import { ValidateStockResponse } from "@/types/inventory";
import { FileUploader, Attachment } from "@/components/shared/FileUploader";
import { currencyAPI, Currency } from "@/services/currencyService";
import type { User as SystemUser } from "@/types/crm";
import { useAuth } from "@/hooks/useAuth";
import { ReassignModal } from "./ReassignModal";
import { EditOpportunityModal } from "./EditOpportunityModal";
import { DocumentPreviewModal } from "@/components/shared/DocumentPreviewModal";
import { QuoteViewer } from "./QuoteViewer";
import type { QuoteData } from "@/services/opportunityService";

type OpportunityCurrency = {
  id: string;
  code: string;
  symbol: string;
};

interface OpportunityDetailProps {
  opportunityId: string | null;
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  onOpportunityDeleted?: (opportunityId: string) => void;
  onOpportunityUpdated?: () => void;
  onEdit?: (opportunity: Opportunity) => void;
  onQuotePreview?: (data: QuoteData) => void;
}

export function OpportunityDetail({
  opportunityId,
  isOpen,
  onClose,
  slug,
  onOpportunityDeleted,
  onOpportunityUpdated,
  onEdit,
  onQuotePreview,
}: OpportunityDetailProps) {
  const { toast } = useToast();
  const { company: authCompany, can } = useAuth();

  const formatNumberWithCommas = (value: string | number): string => {
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numValue)) return "0,00";

    return numValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [responsibleUser, setResponsibleUser] = useState<SystemUser | null>(
    null,
  );
  const [allUsers, setAllUsers] = useState<SystemUser[]>([]);
  const [loading, setLoading] = useState({
    opportunity: false,
    user: false,
    currency: false,
  });
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [isFormalizing, setIsFormalizing] = useState(false);
  const [stages, setStages] = useState<OpportunityStage[]>([]);
  const [discount, setDiscount] = useState<{
    value: string;
    type: "fixed" | "percentage";
  } | null>(null);
  const [cleanNotes, setCleanNotes] = useState("");
  const [currency, setCurrency] = useState<OpportunityCurrency | null>(null);
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isApprovalLoading, setIsApprovalLoading] = useState(false);
  const [isCloningLoading, setIsCloningLoading] = useState(false);
  const [isSendingQuote, setIsSendingQuote] = useState(false);
  const [isFormalizeDialogOpen, setIsFormalizeDialogOpen] = useState(false);
  const [formalizeData, setFormalizeData] = useState({ 
    approver_name: "", 
    signature_url: "",
    approval_date: new Date().toISOString().split('T')[0]
  });
  const [formalizeAttachments, setFormalizeAttachments] = useState<Attachment[]>([]);
  const [isAddingAttachment, setIsAddingAttachment] = useState(false);
  const [newAttachments, setNewAttachments] = useState<Attachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview"); // For multiple tabs
  const [pendingDecision, setPendingDecision] = useState<'approve' | 'reject' | null>(null);
  const [approvalComments, setApprovalComments] = useState("");
  const [versions, setVersions] = useState<Opportunity[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [isLostReasonOpen, setIsLostReasonOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [quotePreviewData, setQuotePreviewData] = useState<QuoteData | null>(null);
  const [pendingLostStage, setPendingLostStage] = useState<OpportunityStage | null>(null);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string; type?: string } | null>(null);
  const [isLoadingQuotePreview, setIsLoadingQuotePreview] = useState(false);
  const [stockValidations, setStockValidations] = useState<Record<string, ValidateStockResponse>>({});
  const [isValidatingStock, setIsValidatingStock] = useState(false);

  const isClosedLost = opportunity?.stage?.stage_type === 'closed_lost';
  const isClosedWon = opportunity?.stage?.stage_type === 'closed_won';
  const isClosed = isClosedLost || isClosedWon;
  // Versión antigua = solo lectura
  const isReadOnly = opportunity?.is_latest_version === false;

  // Optimización: Memorizar currencySymbol para evitar lecturas de localStorage en cada render
  const currencySymbol = useMemo(() => {
    if ((authCompany as any)?.currency) return (authCompany as any).currency;
    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {}
    return "$";
  }, [authCompany]);
 
  const latestVersionId = useMemo(() => {
    return versions.find(v => v.is_latest_version)?.id || opportunityId;
  }, [versions, opportunityId]);

  const fetchOpportunityVersions = async () => {
    if (!opportunityId) return;
    setIsLoadingVersions(true);
    try {
      console.log("Fetching versions for:", opportunityId);
      const res = await opportunityAPI.getOpportunityVersions(opportunityId, slug);
      if (res.success && res.data) {
        // Asegurarnos de que el orden sea correcto
        const sortedVersions = [...res.data].sort((a, b) => {
          if (b.version_number !== a.version_number) return b.version_number - a.version_number;
          return (b.minor_version || 0) - (a.minor_version || 0);
        });
        setVersions(sortedVersions);
      }
    } catch (error) {
      console.error("Error fetching versions:", error);
      // Mantener versiones actuales si hay error
    } finally {
      setIsLoadingVersions(false);
    }
  };

  useEffect(() => {
    if (isOpen && opportunityId && slug) {
      setIsInitialLoading(true);
      setOpportunity(null);
      setCurrency(null);
      setResponsibleUser(null);
      setDiscount(null);
      setCleanNotes("");

      fetchOpportunityDetails();
      fetchOpportunityVersions();
    } else if (!isOpen) {
      setOpportunity(null);
      setCurrency(null);
      setResponsibleUser(null);
      setDiscount(null);
      setCleanNotes("");
      setIsInitialLoading(false);
    }
  }, [isOpen, opportunityId, slug]);

  const handleRequestApproval = async () => {
    if (!opportunity || !opportunityId) return;
    try {
      setIsApprovalLoading(true);
      const res = await opportunityAPI.requestApproval(opportunityId, slug);
      if (res.success) {
        toast({ title: "Éxito", description: "Solicitud de aprobación enviada" });
        fetchOpportunityDetails();
        if (onOpportunityUpdated) onOpportunityUpdated();
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setIsApprovalLoading(false);
    }
  };

  const handleApprovalDecision = async (decision: "approve" | "reject", comments?: string) => {
    if (!opportunity || !opportunityId) return;
    try {
      setIsApprovalLoading(true);
      const res = await opportunityAPI.handleApprovalDecision(opportunityId, slug, decision, comments);
      if (res.success) {
        toast({ title: "Éxito", description: decision === "approve" ? "Cotización aprobada" : "Cotización rechazada" });
        setPendingDecision(null);
        setApprovalComments("");
        fetchOpportunityDetails();
        if (onOpportunityUpdated) onOpportunityUpdated();
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setIsApprovalLoading(false);
    }
  };

  const handleCloneVersion = async () => {
    if (!opportunity || !opportunityId) return;
    try {
      setIsCloningLoading(true);
      const res = await opportunityAPI.cloneOpportunity(opportunityId, slug);
      if (res.success) {
        toast({ title: "Éxito", description: "Nueva versión creada correctamente" });
        // After cloning, we might want to stay in the current one or navigate to the new one.
        // Assuming we navigate to the new one:
        if (onOpportunityUpdated) onOpportunityUpdated();
        onClose(); // Close detail as we moved to a new record
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setIsCloningLoading(false);
    }
  };

  const handleSendQuote = async () => {
    if (!opportunity || !opportunityId) return { success: false, message: "Faltan datos de la oportunidad" };
    try {
      setIsSendingQuote(true);
      const res = await opportunityAPI.sendQuoteEmail(opportunityId, slug);
      if (res.success) {
        return { success: true, message: "Cotización enviada exitosamente por correo electrónico al contacto." };
      } else {
        return { success: false, message: res.message || "No se pudo enviar la cotización." };
      }
    } catch (e: any) {
      return { success: false, message: e.message || "Ocurrió un error al enviar el correo." };
    } finally {
      setIsSendingQuote(false);
    }
  };

  const handlePreviewQuote = async () => {
    if (!opportunity || !opportunityId) {
      toast({ title: "Error", description: "No se pudo identificar la cotización", variant: "destructive" });
      return;
    }
    
    try {
      setIsLoadingQuotePreview(true);
      toast({ title: "Generando previsualización", description: "Cargando datos de la cotización..." });
      
      const data = await opportunityAPI.getQuoteData(opportunityId, slug);
      
      setQuotePreviewData(data);
      
      if (onQuotePreview) {
        onQuotePreview(data);
      }
    } catch (e: any) {
      console.error("❌ [QuotePreview] Error:", e);
      toast({ title: "Error al cargar la cotización", description: e.message, variant: "destructive" });
    } finally {
      setIsLoadingQuotePreview(false);
    }
  };

  const handleFormalize = async () => {
    if (!opportunity || !opportunityId || !formalizeData.approver_name) return;
    
    try {
      setIsFormalizing(true);
      
      // 1. Subir archivos si existen
      if (formalizeAttachments.length > 0) {
        for (const att of formalizeAttachments) {
          await opportunityAPI.uploadAttachment(opportunityId, slug, att.file, att.description || 'Evidencia de Cierre');
        }
      }

      // 2. Formalizar la venta
      const res = await opportunityAPI.formalizeSale(
        opportunityId, 
        slug, 
        formalizeData.approver_name, 
        formalizeData.signature_url
      );

      if (res.success) {
        toast({ title: "¡Éxito!", description: "Venta formalizada correctamente. La cotización ahora está GANADA." });
        setIsFormalizeDialogOpen(false);
        setFormalizeAttachments([]);
        fetchOpportunityDetails();
        if (onOpportunityUpdated) onOpportunityUpdated();
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setIsFormalizing(false);
    }
  };
  
  const handleAddAttachment = async () => {
    if (newAttachments.length === 0 || !opportunityId) return;
    
    try {
      setIsUploading(true);
      toast({ title: "Subiendo archivos", description: `Preparando ${newAttachments.length} archivo(s)...` });
      
      for (const att of newAttachments) {
        await opportunityAPI.uploadAttachment(opportunityId, slug, att.file, att.description || 'Archivo adjunto');
      }
      
      toast({ title: "Éxito", description: "Archivos subidos correctamente" });
      setIsAddingAttachment(false);
      setNewAttachments([]);
      
      // Recargar datos para ver los nuevos adjuntos
      fetchOpportunityDetails(opportunityId);
    } catch (e: any) {
      console.error("❌ [AddAttachment] Error:", e);
      toast({ title: "Error al subir archivos", description: e.message, variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };


  const getApprovalBadge = (status?: string) => {
    switch (status) {
      case "draft":
        return <Badge variant="secondary" className="bg-gray-100 text-gray-700">Borrador</Badge>;
      case "pending":
        return <Badge variant="secondary" className="bg-amber-100 text-amber-700 animate-pulse">Pendiente de Aprobación</Badge>;
      case "approved":
        return <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">Aprobada</Badge>;
      case "rejected":
        return <Badge variant="secondary" className="bg-rose-100 text-rose-700">Rechazada</Badge>;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (opportunity) {
      const notes = opportunity.notes || "";
      const discountMatch = notes.match(/\[DISCOUNT:({.*})\]/);
      if (discountMatch) {
        try {
          const parsed = JSON.parse(discountMatch[1]);
          setDiscount(parsed);
          setCleanNotes(notes.replace(discountMatch[0], "").trim());
        } catch (e) {
          setDiscount(null);
          setCleanNotes(notes);
        }
      } else {
        setDiscount(null);
        setCleanNotes(notes);
      }
    }
  }, [opportunity]);

  const fetchOpportunityDetails = async (overrideId?: string) => {
    const idToFetch = overrideId || opportunityId;
    if (!idToFetch) return;

    try {
      // Optimización: Carga paralela de cotización y usuarios
      setLoading({ opportunity: true, currency: false, user: true });

      const [opportunityResponse, usersResponse] = await Promise.all([
        opportunityAPI.getOpportunityById(idToFetch, slug),
        usersService.getUsers(slug, 1, 1000, true).catch(() => ({ data: [] })),
      ]);

      const opportunityData = opportunityResponse.data;
      setOpportunity(opportunityData);
      setCurrency(opportunityData.currency || null);
      setAllUsers(usersResponse.data);

      // Determinar el responsable (Priorizar objeto embebido sobre búsqueda en lista)
      const responsibleId = opportunityData.responsible_id || opportunityData.created_by;
      
      if (opportunityData.creator && (opportunityData.creator.id === responsibleId || !responsibleId)) {
        setResponsibleUser(opportunityData.creator as SystemUser);
      } else if (responsibleId) {
        const foundUser = usersResponse.data.find(
          (u: SystemUser) => u.id === responsibleId,
        );
        if (foundUser) {
          setResponsibleUser(foundUser);
        } else {
          setResponsibleUser(null);
        }
      } else {
        setResponsibleUser(null);
      }

      // CORRECCIÓN: Cargar las etapas específicas del pipeline de esta cotización
      if (opportunityData.pipeline_id) {
        try {
          const stagesResponse = await opportunityAPI.getStages({
            slug,
            is_active: true,
            pipeline_id: opportunityData.pipeline_id,
          });
          setStages(stagesResponse.data);
        } catch (stagesError) {
          // Silently fail or minimal log
        }
      }

      setIsInitialLoading(false);
    } catch (error: any) {
      toast({
        title: "Error al cargar cotización",
        description: error.message || "No se pudo cargar la información",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, opportunity: false, user: false }));
    }
  };

  const handleStatusChange = async (status: "won" | "lost") => {
    if (!opportunity) return;

    // Mover directamente a closed_won o closed_lost (sin estado pendiente intermedio)
    const stageType = status === "won" ? "closed_won" : "closed_lost";
    const targetStage = stages.find((s) => s.stage_type === stageType);

    if (!targetStage) {
      toast({
        title: "Acción no disponible",
        description: `No se encontró una etapa de tipo "${status === "won" ? "Ganada" : "Perdida"}" en el pipeline. Contacta al administrador.`,
        variant: "destructive",
      });
      return;
    }

    if (opportunity.stage_id === targetStage.id) {
      toast({
        title: "Información",
        description: `La cotización ya se encuentra en la etapa "${targetStage.name}".`,
      });
      return;
    }

    if (status === "lost") {
      setPendingLostStage(targetStage);
      setIsLostReasonOpen(true);
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, opportunity: true }));
      await opportunityAPI.moveOpportunityStage(opportunity.id, {
        slug,
        stage_id: targetStage.id,
      });

      toast({
        title: "¡Cotización Ganada!",
        description: `La cotización ha sido movida a "${targetStage.name}". Se ocultará automáticamente en 24 horas.`,
        className: "bg-green-50 border-green-200 text-green-800",
      });

      if (onOpportunityUpdated) {
        onOpportunityUpdated();
      } else {
        fetchOpportunityDetails();
      }
    } catch (error: any) {
      toast({
        title: "Error al actualizar estado",
        description:
          error.message || "No se pudo cambiar el estado de la cotización.",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, opportunity: false }));
    }
  };

  const confirmLostStatus = async () => {
    if (!opportunity || !pendingLostStage || !lostReason.trim()) return;

    try {
      setLoading((prev) => ({ ...prev, opportunity: true }));
      await opportunityAPI.moveOpportunityStage(opportunity.id, {
        slug,
        stage_id: pendingLostStage.id,
        lost_reason: lostReason,
      });

      toast({
        title: "Cotización Perdida",
        description: `La cotización ha sido movida a "${pendingLostStage.name}".`,
      });

      setIsLostReasonOpen(false);
      setLostReason("");
      
      if (onOpportunityUpdated) {
        onOpportunityUpdated();
      } else {
        fetchOpportunityDetails();
      }
    } catch (error: any) {
      toast({
        title: "Error al actualizar estado",
        description: error.message || "No se pudo marcar la cotización como perdida.",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, opportunity: false }));
    }
  };

  const handleCheckStockAvailability = async () => {
    if (!opportunity?.items || opportunity.items.length === 0) return;
    
    setIsValidatingStock(true);
    const newValidations: Record<string, ValidateStockResponse> = {};
    
    try {
      // Intentamos validar para cada item si tiene estaciones asociadas
      // Nota: En una implementación real, esto consultaría qué estaciones usa cada producto.
      // Por ahora, como es una "mecha", validaremos contra estaciones comunes si se encuentran.
      
      const stationsResponse = await inventoryService.getStationConfigurations(slug);
      const allStationConfigs = stationsResponse.data?.data || [];
      const uniqueStations = Array.from(new Set(allStationConfigs.map((c: any) => c.station_name)));

      for (const item of opportunity.items) {
        // Buscamos si este producto tiene requerimientos en alguna estación
        const productStations = allStationConfigs
          .filter((c: any) => c.inventoryItem?.id === item.product_id || c.inventory_id === item.product_id)
          .map((c: any) => c.station_name);
        
        const stationToCheck = productStations[0] || uniqueStations[0]; // Usamos la primera encontrada o la primera del sistema
        
        if (stationToCheck) {
          try {
            const res = await inventoryService.validateStock({
              slug,
              company_id: authCompany?.id || "",
              station_name: stationToCheck,
              produced_units: item.quantity
            });
            newValidations[item.id] = res.data?.data;
          } catch (e) {
            console.error(`Error validating stock for item ${item.id}`, e);
          }
        }
      }
      setStockValidations(newValidations);
      toast({ title: "Validación completada", description: "Se ha verificado la disponibilidad de materiales." });
    } catch (error) {
      toast({ title: "Error", description: "No se pudo completar la validación de stock", variant: "destructive" });
    } finally {
      setIsValidatingStock(false);
    }
  };

  const handleDeleteOpportunity = async () => {
    if (!opportunityId) return;

    try {
      setIsDeleting(true);
      await opportunityAPI.deleteOpportunity(opportunityId, slug);
      toast({
        title: "Cotización eliminada",
        description: "La cotización se ha eliminado correctamente.",
      });
      setIsDeleteOpen(false);
      onClose();
      if (onOpportunityDeleted) onOpportunityDeleted(opportunityId);
    } catch (error: any) {
      toast({
        title: "Error al eliminar la cotización",
        description: error.message || "No se pudo eliminar la cotización",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getStockStatusBadge = (itemId: string) => {
    const validation = stockValidations[itemId];
    if (!validation) return null;

    if (validation.is_available) {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Stock Disponible
        </Badge>
      );
    }

    const missingCount = validation.missing_items.length;
    return (
      <Badge className="bg-rose-100 text-rose-700 border-rose-200 flex items-center gap-1">
        <AlertCircle className="h-3 w-3" />
        Faltan {missingCount} insumos
      </Badge>
    );
  };

  if (isInitialLoading || !opportunity) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Skeleton className="h-12 w-12 rounded-lg" />
                <div className="space-y-2">
                  <Skeleton className="h-7 w-[300px]" />
                  <Skeleton className="h-5 w-[200px]" />
                </div>
              </div>
              <div className="flex gap-2 mr-12">
                <Skeleton className="h-9 w-[100px]" />
                <Skeleton className="h-9 w-[100px]" />
              </div>
            </div>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="border shadow-sm">
                <CardHeader className="bg-slate-50 border-b">
                  <div className="flex items-center space-x-2">
                    <Skeleton className="h-5 w-5 rounded" />
                    <Skeleton className="h-5 w-[180px]" />
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-[120px]" />
                        <Skeleton className="h-8 w-[150px]" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-[140px]" />
                        <div className="flex items-center space-x-2">
                          <Skeleton className="h-14 w-14 rounded-lg" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-6 w-[80px]" />
                            <Skeleton className="h-2 w-full rounded-full" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-[160px]" />
                        <div className="flex items-center space-x-2">
                          <Skeleton className="h-14 w-14 rounded-lg" />
                          <Skeleton className="h-6 w-[120px]" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-[100px]" />
                        <div className="flex items-center space-x-3 p-2 bg-slate-50 rounded-lg">
                          <Skeleton className="h-10 w-10 rounded-full" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-[120px]" />
                            <Skeleton className="h-3 w-[160px]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border shadow-sm">
                <CardHeader className="bg-slate-50 border-b">
                  <div className="flex items-center space-x-2">
                    <Skeleton className="h-5 w-5 rounded" />
                    <Skeleton className="h-5 w-[220px]" />
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="p-4 border rounded-lg space-y-2">
                        <Skeleton className="h-4 w-[200px]" />
                        <Skeleton className="h-3 w-[150px]" />
                        <div className="flex justify-between items-center pt-2">
                          <Skeleton className="h-4 w-[80px]" />
                          <Skeleton className="h-5 w-[100px]" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="border shadow-sm">
                <CardHeader className="bg-slate-50 border-b">
                  <div className="flex items-center space-x-2">
                    <Skeleton className="h-5 w-5 rounded" />
                    <Skeleton className="h-5 w-[140px]" />
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Skeleton className="h-14 w-14 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-5 w-[150px]" />
                        <Skeleton className="h-4 w-[100px]" />
                      </div>
                    </div>
                    <div className="space-y-2 pt-4 border-t">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-[80%]" />
                      <Skeleton className="h-4 w-[90%]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border shadow-sm">
                <CardHeader className="bg-slate-50 border-b">
                  <div className="flex items-center space-x-2">
                    <Skeleton className="h-5 w-5 rounded" />
                    <Skeleton className="h-5 w-[150px]" />
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Skeleton className="h-12 w-12 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-[120px]" />
                        <Skeleton className="h-3 w-[100px]" />
                      </div>
                    </div>
                    <div className="space-y-2 pt-4 border-t">
                      <Skeleton className="h-4 w-[80%]" />
                      <Skeleton className="h-4 w-[70%]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border shadow-sm">
                <CardHeader className="bg-slate-50 border-b">
                  <div className="flex items-center space-x-2">
                    <Skeleton className="h-5 w-5 rounded" />
                    <Skeleton className="h-5 w-[180px]" />
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-3">
                  <div className="space-y-2">
                    <Skeleton className="h-3 w-[80px]" />
                    <Skeleton className="h-4 w-[140px]" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-3 w-[120px]" />
                    <Skeleton className="h-4 w-[140px]" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="flex justify-between items-center pt-6 border-t">
            <Skeleton className="h-9 w-[100px]" />
            <div className="flex gap-2">
              <Skeleton className="h-9 w-[90px]" />
              <Skeleton className="h-9 w-[90px]" />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const getStageColor = (stageType: string) => {
    switch (stageType) {
      case "start":
        return "bg-blue-500 hover:bg-blue-600";
      case "progress":
        return "bg-yellow-500 hover:bg-yellow-600";
      case "closed_won":
        return "bg-green-500 hover:bg-green-600";
      case "closed_lost":
        return "bg-red-500 hover:bg-red-600";
      default:
        return "bg-gray-500 hover:bg-gray-600";
    }
  };

  const getStageLabel = (stageType: string, stageName: string) => {
    switch (stageType) {
      case "start":
        return "Inicio";
      case "progress":
        return "En Progreso";
      case "closed_won":
        return "Ganada";
      case "closed_lost":
        return "Perdida";
      default:
        return stageName || "Sin etapa";
    }
  };

  const getProbabilityColor = (probability: number) => {
    if (probability >= 75) return "text-green-600";
    if (probability >= 50) return "text-yellow-600";
    if (probability >= 25) return "text-orange-600";
    return "text-red-600";
  };

  const getProbabilityBadge = (probability: number) => {
    if (probability >= 75)
      return "bg-green-100 text-green-800 border-green-300";
    if (probability >= 50)
      return "bg-yellow-100 text-yellow-800 border-yellow-300";
    if (probability >= 25)
      return "bg-orange-100 text-orange-800 border-orange-300";
    return "bg-red-100 text-red-800 border-red-300";
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "Grande":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "Mediana":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Pequeña":
        return "bg-slate-100 text-slate-800 border-slate-200";
      default:
        return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  const getResponsibleDisplayName = (user: SystemUser): string => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ""} ${user.last_name || ""}`.trim();
    }
    return user.nombre || user.name || "Usuario";
  };

  const getUserInitials = (user: SystemUser): string => {
    const name = getResponsibleDisplayName(user);
    if (!name) return "U";

    const parts = name.split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "0000-00-00" || dateString === "null") return "Sin fecha definida";
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) {
        const fallbackDate = new Date(dateString);
        if (!isValid(fallbackDate)) return "Sin fecha definida";
        return format(fallbackDate, "dd MMMM yyyy", { locale: es });
      }
      return format(date, "dd MMMM yyyy", { locale: es });
    } catch (error) {
      return "Sin fecha definida";
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "";
    try {
      const date = parseISO(dateString);
      return format(date, "dd/MM/yyyy HH:mm", { locale: es });
    } catch (error) {
      const date = new Date(dateString);
      return format(date, "dd/MM/yyyy HH:mm", { locale: es });
    }
  };

  const formatRelativeTime = (dateString: string) => {
    if (!dateString) return "";
    try {
      const date = parseISO(dateString);
      const today = new Date();

      const dateNormalized = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      );
      const todayNormalized = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      );

      const diffTime = dateNormalized.getTime() - todayNormalized.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return "hoy";
      } else if (diffDays === 1) {
        return "mañana";
      } else if (diffDays === -1) {
        return "ayer";
      } else if (diffDays > 0) {
        return `en ${diffDays} días`;
      } else {
        return `hace ${Math.abs(diffDays)} días`;
      }
    } catch (error) {
      const date = new Date(dateString);
      const today = new Date();

      const dateNormalized = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      );
      const todayNormalized = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      );

      const diffTime = dateNormalized.getTime() - todayNormalized.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return "hoy";
      } else if (diffDays === 1) {
        return "mañana";
      } else if (diffDays === -1) {
        return "ayer";
      } else if (diffDays > 0) {
        return `en ${diffDays} días`;
      } else {
        return `hace ${Math.abs(diffDays)} días`;
      }
    }
  };

  const getDaysRemaining = (closeDateString: string) => {
    if (!closeDateString || closeDateString === "0000-00-00" || closeDateString === "null") return null;
    try {
      const closeDate = parseISO(closeDateString);
      if (!isValid(closeDate)) {
        const d = new Date(closeDateString);
        if (!isValid(d)) return null;
        const diff = d.getTime() - new Date().setHours(0,0,0,0);
        return Math.round(diff / (1000 * 60 * 60 * 24));
      }
      const today = new Date().setHours(0,0,0,0);
      const diffTime = closeDate.getTime() - today;
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      return isNaN(diffDays) ? null : diffDays;
    } catch (error) {
      return null;
    }
  };

  const daysRemaining = getDaysRemaining(opportunity.estimated_close_date);

  interface SafeCompany {
    id: string;
    name: string;
    logo?: string;
    industry?: string;
    email?: string;
    phone?: string;
    website?: string;
    address?: string;
    [key: string]: any;
  }

  interface SafeContact {
    id: string;
    name: string;
    email?: string;
    position?: string;
    phone?: string;
    avatar?: string;
    [key: string]: any;
  }

  const getCompany = (): SafeCompany => {
    const defaultCompany: SafeCompany = { id: "", name: "Sin empresa" };
    if (!opportunity.client_company) return defaultCompany;

    if (typeof opportunity.client_company === "string") {
      return { ...defaultCompany, name: opportunity.client_company };
    }

    return opportunity.client_company as SafeCompany;
  };

  const getContact = (): SafeContact => {
    const defaultContact: SafeContact = { id: "", name: "Sin contacto" };
    if (!opportunity.contact) return defaultContact;

    if (typeof opportunity.contact === "string") {
      return { ...defaultContact, name: opportunity.contact };
    }

    return opportunity.contact as SafeContact;
  };

  const company = getCompany();
  const contact = getContact();

  const getSourceName = (): string => {
    if (!opportunity.opportunity_source) return "";

    if (typeof opportunity.opportunity_source === "string") {
      return opportunity.opportunity_source;
    }

    return opportunity.opportunity_source.name || "";
  };

  const sourceName = getSourceName();

  const calculateTotals = () => {
    if (!opportunity?.items)
      return { subtotal: 0, productsSubtotal: 0, extrasSubtotal: 0, discountAmount: 0, total: 0 };

    let productsSubtotal = 0;
    let extrasSubtotal = 0;
    let subtotal = 0;

    opportunity.items.forEach((item: any) => {
      const lineTotal = parseFloat(item.line_total) || 0;
      const itemBasePrice = parseFloat(item.base_price) || 0;
      const itemQuantity = item.quantity || 0;
      const lineBaseSubtotal = itemBasePrice * itemQuantity;
      
      productsSubtotal += lineBaseSubtotal;
      extrasSubtotal += (lineTotal - lineBaseSubtotal);
      subtotal += lineTotal;
    });

    let discountAmount = 0;
    if (discount) {
      const val = parseFloat(discount.value) || 0;
      if (discount.type === "percentage") {
        discountAmount = (subtotal * val) / 100;
      } else {
        discountAmount = val;
      }
    }

    return {
      subtotal,
      productsSubtotal,
      extrasSubtotal,
      discountAmount,
      total: Math.max(0, subtotal - discountAmount),
    };
  };

  const { subtotal, productsSubtotal, extrasSubtotal, discountAmount, total } = calculateTotals();

  const hasDiscount = discountAmount > 0;
  const hasRules = opportunity.items?.some(
    (item: any) => item.applied_rules && item.applied_rules.length > 0,
  );

  const calculateItemDiscount = (lineTotal: number, totalSubtotal: number) => {
    if (!discount || totalSubtotal === 0) return 0;
    const val = parseFloat(discount.value) || 0;
    if (discount.type === "percentage") {
      return (lineTotal * val) / 100;
    } else {
      return (lineTotal / totalSubtotal) * val;
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-50 rounded-lg">
                  <Target className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <DialogTitle className="text-2xl font-bold text-slate-900">
                    {opportunity?.name || "Cargando..."}
                  </DialogTitle>
                  <div className="flex items-center space-x-2 mt-1">
                    <Badge
                      className={`${getStageColor(
                        opportunity?.stage?.stage_type || "",
                      )} text-white font-medium`}
                    >
                      {getStageLabel(
                        opportunity?.stage?.stage_type || "",
                        opportunity?.stage?.name || "",
                      )}
                    </Badge>
                    {(opportunity?.version_number !== undefined || opportunity?.minor_version !== undefined) && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        V{opportunity?.version_number || 1}.{opportunity?.minor_version ?? 0}
                      </Badge>
                    )}
                    {opportunity?.is_latest_version ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Versión Actual
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        Versión Anterior
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              {!isClosed && !isReadOnly && (
                <div className="flex gap-2 mr-12">
                  {/* Nuevas Acciones de Backend */}

                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 text-slate-600 hover:text-slate-700 hover:bg-slate-50 border-slate-200"
                    onClick={handlePreviewQuote}
                    disabled={isSendingQuote}
                  >
                    <EyeIcon className="h-4 w-4" />
                    Visualizar
                  </Button>



                  {opportunity.approval_status === "draft" && (
                     <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-amber-200"
                      onClick={handleRequestApproval}
                      disabled={isApprovalLoading}
                    >
                      <ShieldCheck className="h-4 w-4" />
                      Solicitar Aprobación
                    </Button>
                  )}

                  {can("opportunities.update") && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-amber-200"
                        onClick={() => setIsReassignModalOpen(true)}
                        disabled={loading.opportunity}
                      >
                        <ArrowRightLeft className="h-4 w-4" />
                        Reasignar
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        onClick={() => handleStatusChange("lost")}
                        disabled={loading.opportunity}
                      >
                        <XCircle className="h-4 w-4" />
                        Perdida
                      </Button>
                      
                      {/* BE-10/FE-09: Solo permitir formalizar si está aprobada o no requiere aprobación */}
                      {(opportunity.approval_status === "approved" || !opportunity.items?.some((item: any) => item.has_deviation)) && (
                        <Button
                          variant="default"
                          size="sm"
                          className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                          onClick={() => setIsFormalizeDialogOpen(true)}
                          disabled={loading.opportunity}
                        >
                          <FileSignature className="h-4 w-4" />
                          Formalizar Venta
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogHeader>

          {/* Banner de Navegación de Versiones Históricas Discreto */}
          {opportunity && isReadOnly && (
            <div className="mx-6 mt-4 bg-amber-50 text-amber-800 p-4 rounded-xl flex items-center justify-between border border-amber-200 shadow-sm animate-in fade-in duration-300">
              <div className="flex items-center gap-4">
                <div className="bg-white p-2.5 rounded-lg border border-amber-200 shadow-sm text-amber-500">
                  <History className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-400 mb-0.5">Versión Histórica (Solo Lectura)</p>
                  <p className="text-sm font-medium">Estás visualizando la <strong>V{opportunity.version_number}.{opportunity.minor_version ?? 0}</strong>. No puedes realizar cambios en esta versión.</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-white text-slate-700 hover:bg-slate-50 font-bold border-slate-200"
                onClick={() => fetchOpportunityDetails(latestVersionId)}
              >
                <ArrowRightLeft className="h-4 w-4 mr-2" />
                Ir a la Versión Actual
              </Button>
            </div>
          )}

          {/* Banner de Motivo de Pérdida */}
          {isClosedLost && opportunity.lost_reason && (
            <div className="mx-6 mt-4 p-5 bg-rose-50 border-2 border-rose-200 rounded-xl flex flex-col gap-3 shadow-md animate-in slide-in-from-top-4 duration-500">
              <div className="flex items-center gap-4">
                <div className="bg-rose-100 p-3 rounded-full text-rose-600 shadow-inner">
                  <XCircle className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-bold text-rose-900 text-lg">Cotización Perdida</h4>
                  <p className="text-sm text-rose-700 font-medium">Esta cotización ha sido marcada como cerrada y perdida.</p>
                </div>
              </div>
              <div className="bg-white/80 p-4 rounded-lg border border-rose-100 mt-1">
                <p className="text-xs font-bold text-rose-400 uppercase tracking-widest mb-1.5">Motivo de la Pérdida:</p>
                <p className="text-sm text-rose-900 leading-relaxed italic">
                  "{opportunity.lost_reason}"
                </p>
              </div>
            </div>
          )}

          {/* Banner de Aprobación */}
          {opportunity.approval_status === "pending" && (can("admin") || can("opportunities.approve")) && (
            <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex flex-col gap-3 shadow-sm animate-in slide-in-from-top-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-amber-100 p-2 rounded-full text-amber-600">
                    <AlertCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-900">Pendiente de Aprobación</h4>
                    <p className="text-sm text-amber-700">Esta cotización requiere tu revisión para avanzar.</p>
                  </div>
                </div>
                {!pendingDecision && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="bg-white border-rose-200 text-rose-600 hover:bg-rose-50"
                      onClick={() => setPendingDecision("reject")}
                      disabled={isApprovalLoading}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Rechazar
                    </Button>
                    <Button 
                      variant="default" 
                      size="sm" 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setPendingDecision("approve")}
                      disabled={isApprovalLoading}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Aprobar
                    </Button>
                  </div>
                )}
              </div>

              {pendingDecision && (
                <div className="space-y-3 pt-3 border-t border-amber-200 animate-in fade-in zoom-in-95 duration-200">
                  <Label className="text-xs font-semibold text-amber-900 uppercase">
                    Motivo del {pendingDecision === 'approve' ? 'Aprobación' : 'Rechazo'}
                  </Label>
                  <Input 
                    placeholder={pendingDecision === 'approve' ? "Comentario opcional..." : "Explica por qué se rechaza..."} 
                    className={`text-sm bg-white border-${pendingDecision === 'approve' ? 'green' : 'red'}-200`}
                    value={approvalComments}
                    onChange={(e) => setApprovalComments(e.target.value)}
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-8 text-xs"
                      onClick={() => {
                        setPendingDecision(null);
                        setApprovalComments("");
                      }}
                      disabled={isApprovalLoading}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      size="sm" 
                      className={`h-8 text-xs ${pendingDecision === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white shadow-sm`}
                      onClick={() => handleApprovalDecision(pendingDecision, approvalComments)}
                      disabled={isApprovalLoading}
                    >
                      {isApprovalLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                      {pendingDecision === 'approve' ? "Confirmar Aprobación" : "Confirmar Rechazo"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}



          {opportunity.approval_status === 'rejected' && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex flex-col gap-2 shadow-sm animate-in fade-in zoom-in-95">
              <div className="flex items-center gap-3">
                <div className="bg-red-500 p-1.5 rounded-full text-white">
                  <XCircle className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">
                    Cotización Rechazada por {opportunity.approver_name || "Administración"}
                  </p>
                  <p className="text-[10px] text-red-600">
                    {opportunity.approval_date ? formatDate(opportunity.approval_date) : ''}
                  </p>
                </div>
              </div>
              {opportunity.approval_comments && (
                <div className="ml-9 text-xs italic text-red-700 bg-red-100/50 p-2 rounded border border-red-100">
                  "{opportunity.approval_comments}"
                </div>
              )}
            </div>
          )}

          {/* Información de Formalización */}
          {opportunity.approver_name && (
            <div className="mx-6 mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
              <FileCheck className="h-6 w-6 text-emerald-600" />
              <div>
                <h4 className="font-semibold text-emerald-900">Venta Formalizada</h4>
                <p className="text-sm text-emerald-700">
                  Aprobado por: <span className="font-bold">{opportunity.approver_name}</span> el {opportunity.updated_at ? formatDate(opportunity.updated_at) : ''}
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="border shadow-sm">
                <CardHeader className="bg-slate-50 border-b">
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <Target className="h-5 w-5 mr-2 text-green-600" />
                    Información General
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-medium text-slate-600 mb-2">
                          Valor Estimado
                        </h3>
                        <div className="flex items-center space-x-2">
                          <div>
                            <span className="text-2xl font-bold text-green-600">
                              {currencySymbol}{" "}
                              {formatNumberWithCommas(
                                opportunity.estimated_value || "0",
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-slate-600 mb-2">
                          Probabilidad de Éxito
                        </h3>
                        <div className="flex items-center space-x-2">
                          <div className="p-2 bg-blue-50 rounded-lg">
                            <TrendingUp
                              className={`h-5 w-5 ${getProbabilityColor(
                                opportunity.success_probability || 0,
                              )}`}
                            />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span
                                className={`text-xl font-bold ${getProbabilityColor(
                                  opportunity.success_probability || 0,
                                )}`}
                              >
                                {opportunity.success_probability || 0}%
                              </span>
                              <Badge
                                variant="outline"
                                className={getProbabilityBadge(
                                  opportunity.success_probability || 0,
                                )}
                              >
                                {opportunity.success_probability || 0 >= 75
                                  ? "Alta"
                                  : opportunity.success_probability || 0 >= 50
                                    ? "Media"
                                    : opportunity.success_probability || 0 >= 25
                                      ? "Baja"
                                      : "Muy baja"}
                              </Badge>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full w-[var(--progress-width)] ${
                                  (opportunity.success_probability || 0) >= 75
                                    ? "bg-green-500"
                                    : (opportunity.success_probability || 0) >=
                                        50
                                      ? "bg-yellow-500"
                                      : (opportunity.success_probability ||
                                            0) >= 25
                                        ? "bg-orange-500"
                                        : "bg-red-500"
                                }`}
                                style={{
                                   "--progress-width": `${
                                     opportunity.success_probability || 0
                                   }%`,
                                 } as React.CSSProperties}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-slate-600 mb-2">
                          Prioridad y Estado
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          <Badge
                            variant="outline"
                            className={`${getPriorityColor(
                              opportunity.priority || "Mediana",
                            )} font-medium`}
                          >
                            <Tag className="h-3 w-3 mr-1" />
                            {opportunity.priority || "Mediana"}
                          </Badge>
                          {getApprovalBadge(opportunity.approval_status)}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-medium text-slate-600 mb-2">
                          Fecha Estimada de Cierre
                        </h3>
                        <div className="flex items-center space-x-2">
                          <div className="p-2 bg-purple-50 rounded-lg">
                            <Calendar className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <span className="text-lg font-semibold text-slate-900">
                              {formatDate(opportunity.estimated_close_date)}
                            </span>
                            {daysRemaining !== null && !isNaN(daysRemaining) && opportunity.estimated_close_date && opportunity.estimated_close_date !== "0000-00-00" && (
                              <div className="flex items-center space-x-2 mt-1">
                                <Badge
                                  variant={
                                    daysRemaining < 0
                                      ? "destructive"
                                      : daysRemaining < 7
                                        ? "outline"
                                        : "default"
                                  }
                                  className="text-xs"
                                >
                                  <Clock className="h-3 w-3 mr-1" />
                                  {daysRemaining > 0
                                    ? `${daysRemaining} días restantes`
                                    : daysRemaining === 0
                                      ? "Vence hoy"
                                      : `Vencida hace ${Math.abs(
                                          daysRemaining,
                                        )} días`}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-slate-600 mb-2">
                          Responsable
                        </h3>
                        {responsibleUser ? (
                          <div className="flex items-center space-x-3 p-2 bg-slate-50 rounded-lg">
                            <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
                              <AvatarFallback className="bg-green-100 text-green-800 font-medium">
                                {getUserInitials(responsibleUser)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">
                                {getResponsibleDisplayName(responsibleUser)}
                              </p>
                              <p className="text-sm text-slate-600">
                                {responsibleUser.email}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-3 p-2 bg-slate-50 rounded-lg">
                            <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
                              <AvatarFallback className="bg-slate-100 text-slate-600">
                                <Users className="h-5 w-5" />
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">
                                {loading.user
                                  ? "Cargando..."
                                  : "Sin responsable asignado"}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {opportunity.collaborators &&
                        opportunity.collaborators.length > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-slate-600 mb-2">
                              Colaboradores
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              {opportunity.collaborators.map((collab) => {
                                const user = allUsers.find(
                                  (u) => u.id === collab.user_id,
                                );
                                if (!user) return null;
                                return (
                                  <div
                                    key={collab.user_id}
                                    className="flex items-center space-x-2 p-1.5 bg-slate-50 rounded-lg border border-slate-100 pr-3"
                                  >
                                    <Avatar className="h-6 w-6 border border-white shadow-sm">
                                      <AvatarFallback className="bg-blue-100 text-blue-800 text-xs font-medium">
                                        {getUserInitials(user)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span className="text-sm text-slate-700 font-medium">
                                      {getResponsibleDisplayName(user)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>

                  {cleanNotes && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-slate-600 mb-2">
                        Notas / Próximo Paso
                      </h3>
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                        <div className="flex items-start space-x-2">
                          <FileText className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <p className="text-slate-900 whitespace-pre-line">
                            {cleanNotes}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                </CardContent>
              </Card>

              <Card className="border shadow-sm">
                <CardHeader className="bg-slate-50 border-b flex flex-row items-center justify-between py-3">
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <ActivityIcon className="h-5 w-5 mr-2 text-indigo-600" />
                    Productos de la Cotización
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleCheckStockAvailability}
                    disabled={isValidatingStock || !opportunity.items?.length}
                    className="h-8 border-slate-200 hover:bg-white"
                  >
                    {isValidatingStock ? (
                      <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                    ) : (
                      <Boxes className="h-3.5 w-3.5 mr-2 text-emerald-600" />
                    )}
                    Validar Stock
                  </Button>
                </CardHeader>
                <CardContent className="pt-6">
                  {!opportunity.items || opportunity.items.length === 0 ? (
                    <div className="bg-slate-50 rounded-lg p-8 border border-dashed flex flex-col items-center justify-center text-center">
                      <div className="p-3 bg-slate-100 rounded-full mb-3">
                        <FileText className="h-6 w-6 text-slate-400" />
                      </div>
                      <p className="text-slate-500 font-medium">
                        No hay productos asociados a esta cotización.
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-left">
                        <thead className="text-xs text-slate-500 bg-slate-50/50">
                          <tr>
                            <th className="px-4 py-3 font-semibold">
                              Producto
                            </th>
                            {hasRules && (
                              <th className="px-4 py-3 font-semibold">
                                Extras
                              </th>
                            )}
                            <th className="px-4 py-3 font-semibold text-center">
                              Cant.
                            </th>
                            <th className="px-4 py-3 font-semibold text-right">
                              P. base
                            </th>
                            <th className="px-4 py-3 font-semibold text-right">
                              Subtotal
                            </th>
                            {hasDiscount && (
                              <th className="px-4 py-3 font-semibold text-right">
                                Descuento
                              </th>
                            )}
                            <th className="px-4 py-3 font-semibold text-right">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {opportunity.items.map((item: any) => {
                            const lineTotal = parseFloat(item.line_total) || 0;
                            const itemBasePrice = parseFloat(item.base_price) || 0;
                            const itemQuantity = item.quantity || 0;
                            const subtotalLinea = itemBasePrice * itemQuantity;
                            const totalExtrasLinea = lineTotal - subtotalLinea;
                            
                            const itemDiscount = hasDiscount
                              ? calculateItemDiscount(lineTotal, subtotal)
                              : 0;
                            const itemFinalTotal = lineTotal - itemDiscount;

                            return (
                              <tr
                                key={item.id}
                                className="hover:bg-slate-50/50 transition-colors"
                              >
                                <td className="px-4 py-4">
                                  <div className="font-medium text-slate-900">
                                    {item.product?.name ||
                                      item.description || "Producto desconocido"}
                                  </div>
                                  <div className="text-xs text-slate-500 mt-0.5 font-mono">
                                    SKU: {item.product?.sku || "N/A"}
                                  </div>
                                </td>
                                {hasRules && (
                                  <td className="px-4 py-4">
                                    <div className="flex flex-col gap-1.5">
                                      {item.applied_rules &&
                                      item.applied_rules.length > 0 ? (
                                        <>
                                          <div className="flex flex-wrap gap-1">
                                            {item.applied_rules.map(
                                              (appliedRule: any, idx: number) => {
                                                let rule = appliedRule.rule;
                                                if (!rule && item.product?.rules) {
                                                  rule = item.product.rules.find(
                                                    (r: any) =>
                                                      r.id === appliedRule.rule_id,
                                                  );
                                                }
                                                if (!rule) rule = appliedRule;

                                                if (!rule || (!rule.rule_name && !rule.name))
                                                  return null;

                                                const modifier = parseFloat(
                                                  rule.price_modifier || rule.modifier,
                                                );
                                                const isPositive = modifier > 0;
                                                const isNegative = modifier < 0;

                                                return (
                                                  <div
                                                    key={idx}
                                                    className="inline-flex items-center px-2 py-0.5 rounded border border-slate-200 bg-white text-[10px] text-slate-600 shadow-sm"
                                                  >
                                                    <span className="font-medium mr-1">
                                                      {rule.rule_name || rule.name}:
                                                    </span>
                                                    <span
                                                      className={
                                                        isPositive
                                                          ? "text-blue-600 font-bold"
                                                          : isNegative
                                                            ? "text-red-600 font-bold"
                                                            : "text-slate-700 font-bold"
                                                      }
                                                    >
                                                      {rule.price_type ===
                                                      "percentage"
                                                        ? `${
                                                            modifier > 0 ? "+" : ""
                                                          }${modifier}%`
                                                        : `${
                                                            modifier > 0 ? "+" : "-"
                                                          }${currencySymbol}${Math.abs(
                                                            modifier,
                                                          )}`}
                                                    </span>
                                                  </div>
                                                );
                                              },
                                            )}
                                          </div>
                                          {Math.abs(totalExtrasLinea) > 0.01 && (
                                            <div className="text-[10px] font-bold text-blue-700 mt-1">
                                              Total Extras: {totalExtrasLinea > 0 ? "+" : ""}{currencySymbol}{formatNumberWithCommas(totalExtrasLinea)}
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <span className="text-xs text-slate-400">
                                          -
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                )}
                                <td className="px-4 py-4 text-center font-medium text-slate-700">
                                  {itemQuantity}
                                </td>
                                <td className="px-4 py-4 text-right text-slate-600">
                                  <div className="flex flex-col items-end">
                                    {item.has_deviation && item.original_price && (
                                      <span className="text-[10px] text-slate-400 line-through">
                                        {currencySymbol}{formatNumberWithCommas(item.original_price)}
                                      </span>
                                    )}
                                    <div className="flex items-center justify-end gap-1">
                                      {currencySymbol}
                                      {formatNumberWithCommas(itemBasePrice)}
                                      {item.has_deviation && (
                                        <AlertCircle
                                          className="h-3.5 w-3.5 text-amber-500"
                                        />
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-right text-slate-600 font-medium">
                                  {currencySymbol}{formatNumberWithCommas(subtotalLinea)}
                                </td>
                                {hasDiscount && (
                                  <td className="px-4 py-4 text-right text-red-600">
                                    - {currencySymbol}
                                    {formatNumberWithCommas(itemDiscount)}
                                  </td>
                                )}
                                <td className="px-4 py-4 text-right">
                                  <span className="font-extrabold text-slate-900 text-base">
                                    {currencySymbol}
                                    {formatNumberWithCommas(
                                      hasDiscount ? itemFinalTotal : lineTotal,
                                    )}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-slate-50/50 font-medium">
                          <tr>
                            <td
                              colSpan={5 + (hasRules ? 1 : 0)}
                              className="px-4 py-2 text-right text-slate-500 text-xs font-bold uppercase tracking-wider"
                            >
                              Subtotal Productos
                            </td>
                            <td className="px-4 py-2 text-right text-slate-600 font-bold">
                              {currencySymbol}
                              {formatNumberWithCommas(productsSubtotal)}
                            </td>
                          </tr>
                          {Math.abs(extrasSubtotal) > 0.01 && (
                            <tr>
                              <td
                                colSpan={5 + (hasRules ? 1 : 0)}
                                className="px-4 py-2 text-right text-blue-500 text-xs font-bold uppercase tracking-wider"
                              >
                                Ajustes / Extras
                              </td>
                              <td className="px-4 py-2 text-right text-blue-600 font-bold">
                                + {currencySymbol}
                                {formatNumberWithCommas(extrasSubtotal)}
                              </td>
                            </tr>
                          )}
                          <tr className="border-t border-slate-200 bg-slate-100/50">
                            <td
                              colSpan={5 + (hasRules ? 1 : 0)}
                              className="px-4 py-3 text-right text-slate-700 font-bold"
                            >
                              Subtotal Neto
                            </td>
                            <td className="px-4 py-3 text-right text-slate-900 font-black">
                              {currencySymbol}
                              {formatNumberWithCommas(subtotal)}
                            </td>
                          </tr>
                          {hasDiscount && (
                            <tr>
                              <td
                                colSpan={5 + (hasRules ? 1 : 0)}
                                className="px-4 py-2 text-right text-red-500 text-xs font-bold uppercase tracking-wider"
                              >
                                Descuento Global
                              </td>
                              <td className="px-4 py-2 text-right text-red-600 font-bold">
                                - {currencySymbol}
                                {formatNumberWithCommas(discountAmount)}
                              </td>
                            </tr>
                          )}
                          <tr className="text-lg bg-green-50/50">
                            <td
                                colSpan={5 + (hasRules ? 1 : 0)}
                              className="px-4 py-6 text-right font-black text-slate-900 uppercase tracking-tight"
                            >
                              Total Final
                            </td>
                            <td className="px-4 py-6 text-right font-black text-green-700 text-2xl">
                              {currencySymbol}
                              {formatNumberWithCommas(total)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="border shadow-sm">
                <CardHeader className="bg-slate-50 border-b py-3">
                  <CardTitle className="text-base font-semibold flex items-center">
                    <Building2 className="h-4 w-4 mr-2 text-blue-600" />
                    Información del Cliente
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-5 space-y-5">
                  {/* Empresa */}
                  <div className="flex items-start space-x-3 pb-4 border-b border-slate-100">
                    <Avatar className="h-12 w-12 border-2 border-white shadow-sm flex-shrink-0">
                      <AvatarFallback className="bg-blue-100 text-blue-800 font-bold">
                        {company.name?.slice(0, 2).toUpperCase() || "CO"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-900 truncate" title={company.name}>
                        {company.name}
                      </h3>
                      <div className="space-y-1.5 mt-2">
                        {company.industry && (
                          <div className="flex items-center text-[11px] text-slate-500">
                            <Tag className="h-3 w-3 mr-1.5 text-slate-400" />
                            {company.industry}
                          </div>
                        )}
                        {company.website && (
                          <div className="flex items-center text-[11px] text-blue-600 hover:underline">
                            <Globe className="h-3 w-3 mr-1.5 text-blue-400" />
                            <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer" className="truncate">
                              {company.website}
                            </a>
                          </div>
                        )}
                        {company.address && (
                          <div className="flex items-start text-[11px] text-slate-500">
                            <MapPin className="h-3 w-3 mr-1.5 text-slate-400 mt-0.5 flex-shrink-0" />
                            <span className="line-clamp-2">{company.address}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Contacto Principal */}
                  <div className="flex items-center space-x-3 pt-1">
                    <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
                      <AvatarFallback className="bg-purple-100 text-purple-800">
                        {contact.name?.[0]?.toUpperCase() || "C"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="text-[10px] font-bold text-purple-600 uppercase tracking-wider mb-0.5">Contacto Principal</p>
                      <h3 className="font-bold text-sm text-slate-900">
                        {contact.name || "Sin nombre"}
                      </h3>
                      {contact.position && (
                        <p className="text-[11px] text-slate-600">
                          {contact.position}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Vías de Comunicación del Contacto */}
                  <div className="space-y-2 pt-4 border-t border-dashed">
                    {contact.email && (
                      <div className="flex items-center text-xs text-slate-600">
                        <Mail className="h-3.5 w-3.5 mr-2 text-slate-400" />
                        <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline truncate">{contact.email}</a>
                      </div>
                    )}
                    {contact.phone && (
                      <div className="flex items-center text-xs text-slate-600">
                        <Phone className="h-3.5 w-3.5 mr-2 text-slate-400" />
                        <a href={`tel:${contact.phone}`} className="text-blue-600 hover:underline">{contact.phone}</a>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Adjuntos y Documentación */}
              <Card className="border shadow-sm">
                <CardHeader className="bg-slate-50 border-b py-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center text-slate-700">
                    <FileText className="h-4 w-4 mr-2 text-indigo-600" />
                    Adjuntos y Documentación
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 rounded-full"
                    onClick={() => setIsAddingAttachment(true)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </CardHeader>
                <CardContent className="pt-5 space-y-4">
                  {(() => {
                    let atts = opportunity.attachments;
                    if (typeof atts === 'string') {
                      try { atts = JSON.parse(atts); } catch(e) { atts = []; }
                    }
                    
                    if (!Array.isArray(atts) || atts.length === 0) {
                      return (
                        <div className="text-center py-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl">
                          <div className="bg-white p-3 rounded-full w-fit mx-auto mb-3 shadow-sm border border-slate-100">
                            <FileText className="h-6 w-6 text-slate-300" />
                          </div>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Sin archivos</p>
                          <p className="text-[10px] text-slate-400 mt-1">No se han adjuntado documentos todavía.</p>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-2">
                        {atts.map((att: any, idx: number) => (
                          <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-xl group hover:bg-white hover:border-indigo-300 transition-all shadow-sm hover:shadow-md">
                            <div className="flex items-center gap-3 overflow-hidden">
                              <div className="p-2 bg-white rounded-lg border border-slate-100 text-indigo-500 shadow-inner">
                                <FileText className="h-4 w-4" />
                              </div>
                              <div className="overflow-hidden">
                                <p className="text-[11px] font-black text-slate-800 truncate" title={att.name}>{att.name}</p>
                                <p className="text-[9px] text-slate-400 truncate uppercase tracking-tighter">{att.description || 'Sin descripción'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 rounded-full"
                                onClick={() => setPreviewFile({ url: att.url, name: att.name, type: att.type })}
                                title="Previsualizar"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              <Card className="border shadow-sm">
                <CardHeader className="bg-slate-50 border-b">
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <History className="h-5 w-5 mr-2 text-blue-600" />
                    Versiones
                  </CardTitle>
                </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-2">
                      <Label className="text-[10px] text-slate-400 uppercase font-black tracking-widest">Historial de Versiones</Label>
                      {isLoadingVersions ? (
                        <div className="flex items-center gap-2 text-slate-400 text-xs py-2">
                          <Loader2 className="h-4 w-4 animate-spin" /> Cargando historial...
                        </div>
                      ) : versions.length <= 1 ? (
                        <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                          <p className="text-[11px] text-blue-600 font-medium">Esta es la única versión disponible.</p>
                          <p className="text-[10px] text-blue-400 mt-1">Las versiones nuevas se crean automáticamente al guardar cambios.</p>
                        </div>
                      ) : (
                        <>
                          <Select 
                            value={opportunity.id} 
                            onValueChange={(val) => {
                              if (val !== opportunity.id) {
                                fetchOpportunityDetails(val);
                              }
                            }}
                          >
                            <SelectTrigger className="w-full bg-slate-50 border-slate-200 h-10 shadow-sm">
                              <SelectValue placeholder="Seleccionar versión">
                                V{opportunity.version_number}.{opportunity.minor_version ?? 0} {opportunity.is_latest_version ? '(Actual)' : ''}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {versions.map((v) => (
                                <SelectItem key={v.id} value={v.id} className="text-xs py-2">
                                  <div className="flex flex-col">
                                    <span className="font-bold">V{v.version_number}.{v.minor_version ?? 0} {v.is_latest_version ? '✓ Actual' : ''}</span>
                                    <span className="text-[10px] text-slate-400">{formatDateTime(v.created_at)}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-slate-400 italic pt-1.5 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {versions.length} {versions.length === 1 ? 'versión disponible' : 'versiones disponibles'}. Las versiones antiguas son de solo lectura.
                          </p>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

              <Card className="border shadow-sm">
                <CardHeader className="bg-slate-50 border-b">
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-slate-600" />
                    Información del Sistema
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Creado el
                    </label>
                    <div className="flex items-center text-sm text-slate-900 mt-1">
                      <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                      {formatDateTime(opportunity.created_at)}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Última actualización
                    </label>
                    <div className="flex items-center text-sm text-slate-900 mt-1">
                      <ActivityIcon className="h-4 w-4 mr-2 text-slate-400" />
                      {formatDateTime(opportunity.updated_at)}
                    </div>
                  </div>
                  {sourceName && (
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Fuente de Cotización
                      </label>
                      <Badge variant="outline" className="mt-1">
                        {sourceName}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="flex justify-between items-center pt-6 border-t">
            <div>
              {can("opportunities.delete") && (
                <Button
                  variant="destructive"
                  onClick={() => setIsDeleteOpen(true)}
                  className="gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Eliminar
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {can("opportunities.update") && !isClosed && !isReadOnly && (opportunity?.approval_status === 'draft' || opportunity?.approval_status === 'rejected') && (
                <Button
                  onClick={() => {
                    if (onEdit && opportunity) {
                      onEdit(opportunity);
                    } else {
                      setIsEditModalOpen(true);
                    }
                  }}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                >
                  <Edit className="h-4 w-4" />
                  Editar
                </Button>
              )}
              <Button
                variant="outline"
                onClick={onClose}
                className="min-w-[100px]"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás absolutamente seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              la cotización{" "}
              <span className="font-bold">{opportunity.name}</span> y eliminará
              sus datos de nuestros servidores.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteOpportunity();
              }}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? "Eliminando..." : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      

      {opportunity && (
        <ReassignModal
          isOpen={isReassignModalOpen}
          onClose={() => setIsReassignModalOpen(false)}
          type="individual"
          currentOwner={
            responsibleUser ||
            ({
              id: "unknown",
              first_name: "Sin",
              last_name: "Asignar",
              email: "",
            } as unknown as SystemUser)
          }
          targetUsers={allUsers}
          slug={slug}
          opportunity={{
            id: opportunity.id,
            name: opportunity.name,
            pipeline_id: opportunity.pipeline_id,
          }}
          onSuccess={() => {
            fetchOpportunityDetails();
          }}
        />
      )}

      {opportunity && (
        <EditOpportunityModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={(newId) => {
            setIsEditModalOpen(false);
            if (newId) {
              // Si se creó una nueva versión, redirigimos o recargamos
              window.location.hash = `#opp=${newId}`;
              window.location.reload();
            } else {
              onOpportunityUpdated?.();
            }
          }}
          slug={slug}
          opportunity={opportunity}
        />
      )}

      {/* Modal de Formalización de Venta */}
      <Dialog open={isFormalizeDialogOpen} onOpenChange={setIsFormalizeDialogOpen}>
        <DialogContent className="sm:max-w-[650px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-emerald-600" />
              Formalizar Cierre de Venta
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="approver_name">Nombre del Aprobador</Label>
                <Input
                  id="approver_name"
                  placeholder="Ej: Juan Pérez"
                  value={formalizeData.approver_name}
                  onChange={(e) => setFormalizeData({ ...formalizeData, approver_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="approval_date">Fecha de Aprobación</Label>
                <Input
                  id="approval_date"
                  type="date"
                  value={formalizeData.approval_date}
                  onChange={(e) => setFormalizeData({ ...formalizeData, approval_date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="signature_url">Referencia de Firma / Orden (Opcional)</Label>
              <Input
                id="signature_url"
                placeholder="Ej: OC-2024-001"
                value={formalizeData.signature_url}
                onChange={(e) => setFormalizeData({ ...formalizeData, signature_url: e.target.value })}
              />
            </div>

            <div className="border-t pt-4 mt-2">
              <FileUploader 
                label="Respaldo Documental (Orden de Compra, Correo, etc.)"
                attachments={formalizeAttachments}
                onAttachmentsChange={setFormalizeAttachments}
                maxFiles={3}
                required={true}
              />
            </div>
            
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-md text-xs text-blue-700">
              <div className="flex gap-2">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                <p>Al confirmar, la cotización se marcará como <strong>GANADA</strong> y se registrará legalmente en el historial.</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setIsFormalizeDialogOpen(false)} disabled={isFormalizing}>
              Cancelar
            </Button>
            <Button 
                variant="default" 
                className="bg-emerald-600 hover:bg-emerald-700" 
                onClick={handleFormalize}
                disabled={!formalizeData.approver_name || formalizeAttachments.length === 0 || isFormalizing}
            >
              {isFormalizing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                "Confirmar Cierre"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogo para Motivo de Pérdida */}
      <Dialog open={isLostReasonOpen} onOpenChange={setIsLostReasonOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Motivo de Pérdida
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="lost_reason">Por favor, indica por qué se perdió esta cotización:</Label>
              <textarea
                id="lost_reason"
                className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Ej: El cliente eligió a la competencia por precio..."
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
              />
            </div>
            <div className="p-3 bg-red-50 border border-red-100 rounded-md text-xs text-red-700">
              <div className="flex gap-2">
                <AlertCircle className="h-4 w-4" />
                <p>Esta acción moverá la cotización a la etapa de <strong>Perdida</strong> y requerirá un motivo obligatorio.</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setIsLostReasonOpen(false)}>
              Cancelar
            </Button>
            <Button 
                variant="destructive" 
                onClick={confirmLostStatus}
                disabled={!lostReason.trim() || loading.opportunity}
            >
              Confirmar Pérdida
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Previsualizador de Documentos (legacy, adjuntos S3) */}
      {previewFile && (
        <DocumentPreviewModal
          isOpen={!!previewFile}
          onClose={() => setPreviewFile(null)}
          fileUrl={previewFile.url}
          fileName={previewFile.name}
          fileType={previewFile.type}
        />
      )}

      {quotePreviewData && (
        <QuoteViewer
          data={quotePreviewData}
          onClose={() => setQuotePreviewData(null)}
          onSendEmail={handleSendQuote} isSending={isSendingQuote}
        />
      )}

      {/* Modal para añadir adjuntos */}
      <Dialog open={isAddingAttachment} onOpenChange={setIsAddingAttachment}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Añadir Adjuntos</DialogTitle>
            <DialogDescription>
              Sube archivos o documentos relevantes para esta cotización.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <FileUploader 
              attachments={newAttachments}
              onAttachmentsChange={setNewAttachments}
              label="Documentos"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddingAttachment(false)} disabled={isUploading}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAddAttachment} 
              disabled={newAttachments.length === 0 || isUploading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Subiendo...
                </>
              ) : (
                "Guardar Archivos"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

